import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  InjectionToken,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, map, Observable, withLatestFrom } from 'rxjs';
import { isFormChanged } from 'src/app/common/guards/save-data-guard/form-value.service';
import { CreateCategoryDialog, MessageLevel } from 'src/app/interfaces';
import { NotificationState } from 'src/app/notifications/notifications.state';
import { Category } from 'src/app/swagger-api';
import { CategoryNodeType } from '../category-helpers.types';
import { DialogActionTypes } from '../dialogs.enum';

export const CATEGORY_CREATION_CONTEXT = new InjectionToken<CreateCategoryDialog>(
  `CATEGORY_CREATION_CONTEXT
`,
);

type CreateCategoryFormGroup = FormGroup<{
  categoryNodeType: FormControl<CategoryNodeType>;
  parentId: FormControl<string | null>;
  categoryTitle: FormControl<string>;
}>;

@Component({
  selector: 'app-dialog-create-category',
  templateUrl: './dialog-create-category.component.html',
  styleUrls: ['./dialog-create-category.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogCreateCategoryComponent extends NotificationState implements OnInit {
  protected notificationLevel: MessageLevel = 'INFO';

  @ViewChild('parentCategoryInput') parentCategoryInput: ElementRef<HTMLInputElement>;

  public readonly context = inject(CATEGORY_CREATION_CONTEXT);

  public form: CreateCategoryFormGroup = new FormGroup({
    categoryNodeType: new FormControl<CategoryNodeType>('Category', Validators.required),
    parentId: new FormControl<string | null>(null),
    categoryTitle: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(4),
      Validators.maxLength(200),
    ]),
  });

  public readonly hasUnsavedChanges$ = isFormChanged(this.form, ['categoryNodeType']);

  public ngOnInit(): void {
    this.initForm();
  }

  public actionsHandler(action: DialogActionTypes): void {
    this.dialogActions[action]();
  }

  public submit(): void {
    this.confirmAction();
  }

  public override confirmAction(): void {
    const categoryNodeType: CategoryNodeType = this.form.get('categoryNodeType').value;

    if (categoryNodeType === 'Category') {
      this.createCategory();
    } else if (categoryNodeType === 'Subcategory') {
      this.createSubcategory();
    } else {
      throw Error('Invalid node type: ' + String(categoryNodeType));
    }
  }

  private createCategory(): void {
    const categoryTitle: string = this.form.get('categoryTitle').value;

    this.context
      .createParent(categoryTitle)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.activeModal.close(true);
      });
  }

  private createSubcategory(): void {
    const subcategoryTitle: string = this.form.get('categoryTitle').value;
    const parentId: string = this.form.get('parentId').value;

    this.context
      .createChild(subcategoryTitle, parentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.activeModal.close(true);
      });
  }

  public get isValid(): boolean {
    return this.form.valid;
  }

  public onParentCategoryInput(_event: Event): void {
    const formControl = this.form.get('parentId');

    // Set the parent id as null when the text is changed, it should only be set when a category is selected
    formControl.setValue(null);
    formControl.markAsTouched();
  }

  public onParentCategoryInputBlur(): void {
    const formControl = this.form.get('parentId');
    formControl.markAsTouched();
  }

  public trimTitle(): void {
    const formControl = this.form.get('categoryTitle');

    const previousValue = formControl.value;

    formControl.setValue(previousValue.trim());

    formControl.markAsTouched();
  }

  public onKeyDown(): void {
    const formControl = this.form.get('categoryTitle');

    formControl.markAsTouched();
  }

  private initForm(): void {
    // This validates that the parent id is required when node type is subcategory

    this.form
      .get('categoryNodeType')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categoryNodeType: CategoryNodeType) => {
        const parentIdControl = this.form.get('parentId');

        if (categoryNodeType === 'Category') {
          parentIdControl.setValidators(null);
          parentIdControl.setValue(null);
        } else {
          parentIdControl.setValidators(Validators.required);
        }

        parentIdControl.updateValueAndValidity();
      });
  }

  public onSelectParentCategory(event: NgbTypeaheadSelectItemEvent<Category>): void {
    const selectedCategory = event.item;
    this.form.get('parentId').setValue(selectedCategory.entity_id);

    if (this.parentCategoryInput) {
      this.parentCategoryInput.nativeElement.value = selectedCategory.title;
    }
  }

  public formatParentCategory = (category: Category): string => {
    return category.title;
  };

  public searchParentCategories = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      withLatestFrom(this.context.existedCategories),
      map(([term, existedCategories]) =>
        term === ''
          ? existedCategories
          : existedCategories
              .filter((category) => category.title.toLowerCase().includes(term.toLowerCase()))
              .slice(0, 10),
      ),
    );
}
